import { useMemo, useState } from "react";
import { Lock, Send } from "lucide-react";
import { Button, ErrorAlert } from "../../../components/ui";
import { useAuthUser } from "../../../stores/authStore";
import type { LimitesAnexo } from "../../../types/portalCliente";
import { mensagemDeErro, useCapacidades, useCriarInteracao } from "./api";
import { validarAnexos } from "./novoChamado";
import { SeletorAnexos } from "./SeletorAnexos";
import { useAnexos } from "./useAnexos";

const LIMITE_COMENTARIO = 2000;

const LIMITES_DE_RESERVA: LimitesAnexo = {
  maxArquivos: 10,
  maxBytesPorArquivo: 10 * 1024 * 1024,
  maxBytesTotal: 25 * 1024 * 1024,
};

/**
 * Resposta no chamado. Só aparece onde a escrita está ligada: com a bandeira
 * desligada a rota de interação nem é registrada, e o envio voltaria 404.
 *
 * A equipe T4i escolhe entre responder ao cliente e deixar nota interna. A
 * escolha muda a cor da caixa, o aviso e o texto do botão, e continua valendo
 * depois do envio: quem escreve várias notas seguidas não passa a mandá-las ao
 * cliente sem perceber.
 */
export const CompositorInteracao = ({ idChamado }: { idChamado: number }) => {
  const capacidades = useCapacidades();
  const criar = useCriarInteracao();
  const equipe = useAuthUser()?.equipe ?? false;

  const [interna, setInterna] = useState(false);
  const [comentario, setComentario] = useState("");
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);

  // Limites de reserva enquanto as capacidades não chegam: o hook precisa deles
  // antes do primeiro render, e o compositor só aparece depois da resposta. O
  // useMemo é o que mantém a identidade estável — sem ele, o ouvinte de colagem
  // seria refeito a cada render.
  const limites: LimitesAnexo = useMemo(
    () => capacidades.data?.anexos ?? LIMITES_DE_RESERVA,
    [capacidades.data]
  );

  const {
    anexos,
    adicionar,
    remover,
    limpar,
    erro: erroAnexos,
    setErro: setErroAnexos,
    arrastando,
  } = useAnexos(limites);

  if (!capacidades.data?.escritasHabilitadas) return null;
  // A API recusa interação sem texto e sem anexo: o botão segue a mesma regra.
  const vazio = comentario.trim().length === 0 && anexos.length === 0;

  const enviar = async () => {
    setErroEnvio(null);
    const problema = validarAnexos(anexos, limites);
    if (problema) {
      setErroAnexos(problema);
      return;
    }

    try {
      await criar.mutateAsync({
        idChamado,
        comentario: comentario.trim(),
        anexos,
        interna: equipe && interna,
      });
      setComentario("");
      limpar();
    } catch (e) {
      setErroEnvio(mensagemDeErro(e, "Não foi possível registrar a interação."));
    }
  };

  const nota = equipe && interna;

  return (
    <div
      className={[
        "mt-4 space-y-3 rounded-xl p-4 transition-colors",
        arrastando
          ? "border border-blue-400 bg-blue-50/40"
          : nota
            ? "border border-dashed border-amber-400 bg-amber-50"
            : "border border-gray-200 bg-white",
      ].join(" ")}
    >
      {equipe && (
        <div
          role="radiogroup"
          aria-label="Tipo de interação"
          className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white text-sm"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!interna}
            onClick={() => setInterna(false)}
            className={[
              "px-3 py-1.5 font-medium transition-colors",
              !interna ? "bg-blue-50 text-blue-800" : "text-gray-600 hover:bg-gray-50",
            ].join(" ")}
          >
            Resposta ao cliente
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={interna}
            onClick={() => setInterna(true)}
            className={[
              "inline-flex items-center gap-1.5 border-l border-gray-300 px-3 py-1.5 font-medium transition-colors",
              interna ? "bg-amber-100 text-amber-900" : "text-gray-600 hover:bg-gray-50",
            ].join(" ")}
          >
            <Lock className="h-3.5 w-3.5" />
            Nota interna
          </button>
        </div>
      )}

      <textarea
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        maxLength={LIMITE_COMENTARIO}
        rows={3}
        placeholder={
          !equipe
            ? "Escreva uma resposta para a equipe…"
            : nota
              ? "Escreva uma nota para a equipe…"
              : "Escreva a resposta ao cliente…"
        }
        className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />

      <SeletorAnexos
        anexos={anexos}
        aoAdicionar={adicionar}
        aoRemover={remover}
        limites={limites}
        arrastando={arrastando}
      />

      {erroAnexos && <p className="text-sm text-red-600">{erroAnexos}</p>}

      {erroEnvio && (
        <ErrorAlert
          title="Não foi possível registrar a interação"
          message={erroEnvio}
          onClose={() => setErroEnvio(null)}
          variant="error"
        />
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-400">
          {comentario.length}/{LIMITE_COMENTARIO}
          {equipe && (
            <span className={nota ? "ml-2 font-medium text-amber-800" : "ml-2 text-gray-500"}>
              {nota
                ? "Só a equipe T4i vê. Não sai por e-mail."
                : "O contato do chamado recebe por e-mail."}
            </span>
          )}
        </span>
        <Button
          type="button"
          onClick={enviar}
          disabled={vazio || Boolean(erroAnexos)}
          loading={criar.isPending}
          className={
            nota
              ? "bg-amber-600 text-white hover:bg-amber-700"
              : "bg-gradient-to-r from-blue-800 to-blue-600 text-white hover:from-blue-900 hover:to-blue-700"
          }
        >
          {nota ? <Lock className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
          {criar.isPending
            ? "Enviando…"
            : !equipe
              ? "Enviar"
              : nota
                ? "Salvar nota interna"
                : "Enviar ao cliente"}
        </Button>
      </div>
    </div>
  );
};

export default CompositorInteracao;
